import { FC } from "react";
import {
  BoxProps,
  Skeleton,
  TabNav,
  TabNavLink,
  TabNavLinkProps,
} from "@hightouch/htui";
import { RouterLink, useLocation } from "src/router";

export type RouteTab = Pick<
  TabNavLinkProps,
  "statusIndicator" | "isDisabled" | "count" | "tooltip"
> & {
  isHidden?: boolean;
  /** Segment this tab matches on, at the strip's depth. */
  path: string;
  title: string;
  /**
   * Absolute path to link to. Needed when the strip renders inside the route
   * it links from, where a relative `path` would resolve under the active tab.
   */
  to?: string;
  /**
   * Carry the current search string over to this tab. Defaults to true; set it
   * false where the params belong to the tab you are leaving.
   */
  preserveSearch?: boolean;
};

type Props = BoxProps & {
  isLoading?: boolean;
  tabs: Array<RouteTab>;
  depth?: number;
  collapsible?: boolean;
};

export const useTabState = <T extends RouteTab>(tabs: Array<T>, depth = 3) => {
  const location = useLocation();
  const pathTabs = location.pathname.split("/");

  const visibleTabs = tabs.filter((tab) => !tab.isHidden);

  const activeTabIndex = visibleTabs.findIndex((tab) =>
    tab.path === "" ? pathTabs.length === depth : tab.path === pathTabs[depth],
  );

  const activeTab = visibleTabs[activeTabIndex];

  return {
    visibleTabs,
    activeTab,
    activeTabIndex,
  };
};

export const RouteTabs: FC<Readonly<Props>> = ({
  tabs,
  depth = 3,
  isLoading = false,
  collapsible = false,
  ...props
}) => {
  const location = useLocation();
  const { visibleTabs, activeTabIndex } = useTabState(tabs, depth);

  return (
    <Skeleton isLoading={isLoading}>
      <TabNav collapsible={collapsible} {...props}>
        {visibleTabs.map((tab, index) => {
          const isActive = index === activeTabIndex;
          return (
            <TabNavLink
              asChild
              key={tab.path === "" ? "index" : tab.path}
              isActive={isActive}
              isDisabled={tab.isDisabled}
              count={tab.count}
              statusIndicator={tab.statusIndicator}
              tooltip={tab.tooltip}
              badgeVariant={isActive ? "info" : "subtle"}
            >
              <RouterLink
                preload={isActive ? undefined : "mount"}
                to={{
                  pathname: tab.to ?? tab.path,
                  // The active tab links to the URL you are already on, so it
                  // keeps its own params either way.
                  search:
                    isActive || tab.preserveSearch !== false
                      ? location.search
                      : "",
                }}
              >
                {tab.title}
              </RouterLink>
            </TabNavLink>
          );
        })}
      </TabNav>
    </Skeleton>
  );
};
